import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Upload, X, Camera, Tag, ArrowLeft } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import BodyMap, { BODY_REGIONS } from "../components/BodyMap";

const REGIONS = BODY_REGIONS;

export default function NewEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const today = new Date().toISOString().slice(0, 10);
  const presetDate = searchParams.get("date");
  const [entryDate, setEntryDate] = useState(/^\d{4}-\d{2}-\d{2}$/.test(presetDate || "") ? presetDate : today);
  const [regions, setRegions] = useState([]);
  const [symptoms, setSymptoms] = useState({
    itching: { value: 3, enabled: true },
    pain: { value: 2, enabled: true },
    redness: { value: 4, enabled: true },
    dryness: { value: 0, enabled: false },
    weeping: { value: 0, enabled: false },
  });
  const [treatments, setTreatments] = useState([{ name: "", dosage: "", applied_at: "", notes: "" }]);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]); // {path, content_type, body_regions: []}
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  // Dashboard-defined regimen + which items the patient applied today
  const [regimen, setRegimen] = useState({ prescribed: [], non_prescribed: [] });
  const [applied, setApplied] = useState(new Set()); // keys: "rx:Name" or "otc:Name"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/treatments-list");
        setRegimen({
          prescribed: data?.prescribed || [],
          non_prescribed: data?.non_prescribed || [],
        });
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data: e } = await api.get(`/entries/${editId}`);
        setEntryDate(e.entry_date || today);
        setRegions(e.body_regions || []);
        const en = e.enabled_symptoms || ["itching", "pain", "redness"];
        setSymptoms({
          itching: { value: e.itching ?? 0, enabled: en.includes("itching") },
          pain:    { value: e.pain    ?? 0, enabled: en.includes("pain") },
          redness: { value: e.redness ?? 0, enabled: en.includes("redness") },
          dryness: { value: e.dryness ?? 0, enabled: en.includes("dryness") },
          weeping: { value: e.weeping ?? 0, enabled: en.includes("weeping") },
        });
        setTreatments((e.treatments && e.treatments.length) ? e.treatments : [{ name: "", dosage: "", applied_at: "", notes: "" }]);
        // Build applied set from any prior treatments in entry
        const appliedKeys = new Set();
        (e.treatments || []).forEach(t => {
          if (t?.name) {
            const looksLikeRx = /(steroid|antibiotic|biologic|isotretinoin|JAK|phototherapy|antifungal|retinoid|calcineurin)/i.test(t.name + " " + (t.category || ""));
            appliedKeys.add(`${looksLikeRx ? "rx" : "otc"}:${t.name}`);
            // also add both possibilities to be safe
            appliedKeys.add(`rx:${t.name}`);
            appliedKeys.add(`otc:${t.name}`);
          }
        });
        setApplied(appliedKeys);
        setNotes(e.notes || "");
        const ph = (e.photos && e.photos.length)
          ? e.photos
          : (e.photo_paths || []).map(p => ({ path: p, body_regions: [] }));
        setPhotos(ph);
      } catch (err) {
        toast.error("Could not load entry");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const sevColor = (v) => v <= 3 ? "bg-emerald-200 text-emerald-900" : v <= 6 ? "bg-amber-200 text-amber-900" : "bg-rose-200 text-rose-900";

  const toggleRegion = (r) => setRegions((curr) => curr.includes(r) ? curr.filter(x => x !== r) : [...curr, r]);

  const onUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const raw of Array.from(files)) {
        const f = await compressImage(raw);
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ ...data, body_regions: regions.length === 1 ? [...regions] : [] });
      }
      setPhotos((p) => [...p, ...uploaded]);
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));
  const togglePhotoRegion = (idx, r) => setPhotos((p) => p.map((ph, i) => {
    if (i !== idx) return ph;
    const cur = ph.body_regions || [];
    return { ...ph, body_regions: cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r] };
  }));

  const addTreatment = () => setTreatments((t) => [...t, { name: "", dosage: "", applied_at: "", notes: "" }]);
  const updateTreatment = (i, k, v) => setTreatments((t) => t.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeTreatment = (i) => setTreatments((t) => t.filter((_, idx) => idx !== i));

  const toggleApplied = (kind, name) => {
    const key = `${kind}:${name}`;
    setApplied((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const buildAppliedTreatments = () => {
    const out = [];
    regimen.prescribed.forEach((t) => {
      if (applied.has(`rx:${t.name}`)) out.push({ name: t.name, category: t.category || "" });
    });
    regimen.non_prescribed.forEach((t) => {
      if (applied.has(`otc:${t.name}`)) out.push({ name: t.name, category: t.category || "" });
    });
    return out;
  };

  const submit = async () => {
    if (regions.length === 0) { toast.error("Select at least one body region"); return; }
    setBusy(true);
    try {
      const enabled = Object.entries(symptoms).filter(([, s]) => s.enabled).map(([k]) => k);
      const payload = {
        entry_date: entryDate,
        body_regions: regions,
        itching: symptoms.itching.value,
        pain: symptoms.pain.value,
        redness: symptoms.redness.value,
        dryness: symptoms.dryness.value,
        weeping: symptoms.weeping.value,
        enabled_symptoms: enabled,
        treatments: buildAppliedTreatments(),
        notes,
        photos: photos.map(p => ({ path: p.path, body_regions: p.body_regions || [] })),
        photo_paths: photos.map(p => p.path),
      };
      const { data } = isEdit
        ? await api.patch(`/entries/${editId}`, payload)
        : await api.post("/entries", payload);
      toast.success(isEdit ? "Entry updated" : "Entry saved");
      navigate(`/entry/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? "Failed to update" : "Failed to save"));
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      {loading ? (
        <p className="text-stone-500" data-testid="edit-entry-loading">Loading entry…</p>
      ) : (
      <>
      <div>
        {isEdit && (
          <Link to={`/entry/${editId}`} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-2" data-testid="edit-back-link">
            <ArrowLeft className="w-3 h-3"/>Back to entry
          </Link>
        )}
        <p className="text-xs uppercase tracking-[0.18em] text-sage-500">{isEdit ? "Update record" : "Today's check-in"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-2" data-testid="entry-page-title">
          {isEdit ? "Edit entry" : "New entry"}
        </h1>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <h3 className="font-serif text-2xl text-stone-900">Photos</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="sm:col-span-2 border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-stone-100 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="photo-upload-zone">
                <Upload className="w-7 h-7 text-sage-400"/>
                <p className="mt-3 text-sm text-stone-600 text-center">{uploading ? "Uploading..." : "Drag photos or click to choose"}</p>
                <p className="mt-1 text-xs text-stone-400">JPG, PNG, WEBP</p>
                <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="photo-input"/>
              </label>
              <label className="border-2 border-dashed border-sage-200 bg-sage-50 hover:bg-sage-100 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="photo-camera-zone">
                <Camera className="w-7 h-7 text-sage-500"/>
                <p className="mt-3 text-sm text-sage-700 text-center font-medium">{uploading ? "Uploading..." : "Take a photo"}</p>
                <p className="mt-1 text-xs text-stone-500">Use rear camera</p>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="photo-camera-input"/>
              </label>
            </div>
            {photos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="uploaded-photos">
                {photos.map((p, i) => (
                  <div key={i} className="relative group" data-testid={`uploaded-photo-${i}`}>
                    <img src={photoUrl(p.path)} alt="" className="w-full h-32 object-cover rounded-xl"/>
                    <button onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-white border border-stone-200 rounded-full p-1 shadow-sm" data-testid={`remove-photo-${i}`}>
                      <X className="w-3 h-3"/>
                    </button>
                    <div className="mt-2 flex flex-wrap gap-1 items-center">
                      {(p.body_regions || []).map((r) => (
                        <Badge key={r} className="bg-sage-100 text-sage-700 border-0 rounded-full text-[10px] font-medium px-2 py-0.5" data-testid={`photo-${i}-tag-${r.toLowerCase()}`}>{r}</Badge>
                      ))}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="inline-flex items-center gap-1 text-[10px] text-sage-600 hover:text-sage-700 px-2 py-0.5 rounded-full border border-dashed border-sage-300 hover:bg-sage-50 transition-colors" data-testid={`photo-${i}-tag-btn`}>
                            <Tag className="w-2.5 h-2.5"/> {(p.body_regions?.length ?? 0) === 0 ? "Tag site" : "Edit"}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-3" align="start">
                          <p className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Body sites</p>
                          <div className="flex flex-wrap gap-1.5" data-testid={`photo-${i}-region-picker`}>
                            {REGIONS.map((r) => {
                              const active = (p.body_regions || []).includes(r);
                              return (
                                <button key={r} onClick={() => togglePhotoRegion(i, r)} className={`px-2.5 py-1 rounded-full text-xs transition-all ${active ? "bg-sage-400 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`} data-testid={`photo-${i}-region-${r.toLowerCase()}`}>
                                  {r}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-serif text-2xl text-stone-900">What areas are affected today?</h3>
              <p className="text-[11px] uppercase tracking-widest text-stone-400">Tap areas on the diagram</p>
            </div>
            <div className="mt-5">
              <BodyMap selected={regions} onToggle={toggleRegion} />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-serif text-2xl text-stone-900">Severity</h3>
              <p className="text-[11px] uppercase tracking-widest text-stone-400">Tick to track · rate 0–10</p>
            </div>
            <div className="mt-6 space-y-5">
              {[
                ["itching", "Itching"],
                ["pain", "Pain"],
                ["redness", "Redness"],
                ["dryness", "Dryness"],
                ["weeping", "Weeping"],
              ].map(([key, label]) => (
                <SymptomRow
                  key={key}
                  label={label}
                  symKey={key}
                  state={symptoms[key]}
                  setState={(next) => setSymptoms((s) => ({ ...s, [key]: next }))}
                  colorFn={sevColor}
                />
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-serif text-2xl text-stone-900">Treatments applied today</h3>
              <Link to="/dashboard" className="text-[11px] uppercase tracking-widest text-stone-400 hover:text-stone-600" data-testid="manage-regimen-link">
                Manage on dashboard ↗
              </Link>
            </div>
            {regimen.prescribed.length === 0 && regimen.non_prescribed.length === 0 ? (
              <p className="mt-4 text-stone-500 italic" data-testid="no-treatments-msg">
                Add some treatments via your dashboard to get started
              </p>
            ) : (
              <div className="mt-5 space-y-5" data-testid="treatments-checklist">
                {regimen.prescribed.length > 0 && (
                  <div data-testid="rx-checklist">
                    <p className="text-xs uppercase tracking-[0.18em] text-sage-500 mb-3">Prescribed</p>
                    <div className="space-y-2">
                      {regimen.prescribed.map((t) => {
                        const key = `rx:${t.name}`;
                        const slug = t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                        const checked = applied.has(key);
                        return (
                          <label key={key} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? "bg-sage-50 border-sage-200" : "bg-stone-50 border-stone-100 hover:bg-stone-100"}`} data-testid={`treatment-row-rx-${slug}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleApplied("rx", t.name)}
                              className="data-[state=checked]:bg-sage-500 data-[state=checked]:border-sage-500"
                              data-testid={`treatment-check-rx-${slug}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-stone-900 truncate">{t.name}</p>
                              {t.category && t.category !== "Other" && (
                                <p className="text-[11px] uppercase tracking-wider text-sage-600">{t.category}</p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                {regimen.non_prescribed.length > 0 && (
                  <div data-testid="otc-checklist">
                    <p className="text-xs uppercase tracking-[0.18em] text-clay-500 mb-3">Non-prescribed</p>
                    <div className="space-y-2">
                      {regimen.non_prescribed.map((t) => {
                        const key = `otc:${t.name}`;
                        const slug = t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                        const checked = applied.has(key);
                        return (
                          <label key={key} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked ? "bg-emerald-50 border-clay-400/40" : "bg-stone-50 border-stone-100 hover:bg-stone-100"}`} data-testid={`treatment-row-otc-${slug}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleApplied("otc", t.name)}
                              className="data-[state=checked]:bg-clay-500 data-[state=checked]:border-clay-500"
                              data-testid={`treatment-check-otc-${slug}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-stone-900 truncate">{t.name}</p>
                              {t.category && t.category !== "Other" && (
                                <p className="text-[11px] uppercase tracking-wider text-clay-500">{t.category}</p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-stone-100">
            <h3 className="font-serif text-2xl text-stone-900">Notes</h3>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How did your skin feel today? Any triggers, food, weather changes?" className="mt-3 bg-stone-50 border-stone-200 rounded-xl min-h-[120px]" data-testid="notes-textarea"/>
          </div>
        </div>

        <aside className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl p-6 border border-stone-100 sticky top-24">
            <Label htmlFor="entry-date">Date</Label>
            <Input id="entry-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl" data-testid="entry-date-input"/>
            <div className="mt-5 text-sm text-stone-500">
              <div className="flex justify-between"><span>Photos</span><span>{photos.length}</span></div>
              <div className="flex justify-between"><span>Regions</span><span>{regions.length}</span></div>
              <div className="flex justify-between"><span>Treatments</span><span>{treatments.filter(t => t.name).length}</span></div>
            </div>
            <Button onClick={submit} disabled={busy} className="w-full mt-6 rounded-full bg-sage-400 hover:bg-sage-500 text-white py-6" data-testid="save-entry-btn">
              {busy ? "Saving..." : (isEdit ? "Save changes" : "Save entry")}
            </Button>
          </div>
        </aside>
      </div>
      </>
      )}
    </AppShell>
  );
}

function SymptomRow({ label, symKey, state, setState, colorFn }) {
  const toggle = () => setState({ ...state, enabled: !state.enabled });
  const setValue = (v) => setState({ ...state, value: v });
  return (
    <div data-testid={`slider-${symKey}`} className={state.enabled ? "" : "opacity-50"}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <Checkbox
            checked={state.enabled}
            onCheckedChange={toggle}
            data-testid={`symptom-${symKey}-toggle`}
            className="data-[state=checked]:bg-sage-500 data-[state=checked]:border-sage-500"
          />
          <span className="text-sm font-medium text-stone-800">{label}</span>
        </label>
        <Badge className={`${state.enabled ? colorFn(state.value) : "bg-stone-100 text-stone-400"} border-0 rounded-full font-mono`} data-testid={`symptom-${symKey}-badge`}>
          {state.enabled ? `${state.value}/10` : "off"}
        </Badge>
      </div>
      <Slider
        value={[state.value]}
        max={10}
        step={1}
        disabled={!state.enabled}
        onValueChange={(v) => setValue(v[0])}
        data-testid={`symptom-${symKey}-slider`}
      />
    </div>
  );
}
